/**
 * 极练 Rehab & Fit —— 周度肌群有效组数统计引擎（阶段 8）。
 *
 * 铁律：
 * 1. 纯函数、零依赖、时间与历史作为参数传入；
 * 2. 精确按每条已完成组记录归属动作的主肌群（primaryMuscles）累加；
 *    复合动作与孤立动作主肌群均记 1 组；
 * 3. 统计近 7 天有效区间 [referenceDate - 6天, referenceDate]；
 * 4. 输出可解释、可序列化、确定性排序。
 */

import type { Exercise } from "@jilian/shared";

export interface MuscleSetItem {
  exerciseId: string;
  completedAt?: string | null; // ISO string 或 YYYY-MM-DD
  status?: string; // "completed"
}

export interface MuscleEffectiveSets {
  muscle: string;
  sets: number;
}

/** 计算给定日期前推 6 天（共 7 天）的起始日期 YYYY-MM-DD */
export function get7DaysStartDate(refDateStr: string): string {
  const d = new Date(`${refDateStr.slice(0, 10)}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() - 6);
  return d.toISOString().slice(0, 10);
}

/**
 * 提取记录归属日期 YYYY-MM-DD
 */
function extractDate(val?: string | null): string | null {
  if (!val) return null;
  return val.slice(0, 10);
}

/**
 * 按主肌群统计近 7 天有效完成组数。
 *
 * @param sets 组记录数组（或包含 exerciseId/completedAt/status 的轻量对象）
 * @param exercisesById 动作库查询 Map
 * @param referenceDate 评估基准日期 YYYY-MM-DD
 * @returns 各主肌群有效完成组数统计列表，按组数降序排列
 */
export function weeklyMuscleSets(
  sets: ReadonlyArray<MuscleSetItem>,
  exercisesById: ReadonlyMap<string, Exercise>,
  referenceDate: string,
): MuscleEffectiveSets[] {
  const refDate = referenceDate.slice(0, 10);
  const startDate = get7DaysStartDate(refDate);

  const countMap = new Map<string, number>();

  for (const item of sets) {
    if (item.status && item.status !== "completed") {
      continue;
    }
    const d = extractDate(item.completedAt);
    if (!d || d < startDate || d > refDate) {
      continue;
    }

    const ex = exercisesById.get(item.exerciseId);
    if (!ex || !ex.primaryMuscles || ex.primaryMuscles.length === 0) {
      continue;
    }

    // 复合动作与孤立动作主肌群各记 1 组
    for (const m of ex.primaryMuscles) {
      countMap.set(m, (countMap.get(m) ?? 0) + 1);
    }
  }

  return Array.from(countMap.entries())
    .map(([muscle, count]) => ({ muscle, sets: count }))
    .sort((a, b) => b.sets - a.sets || a.muscle.localeCompare(b.muscle));
}
